import {
  type IManufacturer,
  type IProductSafety,
  type IProductSafetyDocument,
  type IResponsiblePerson,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { type ILanguageState } from '../../reducers/language/types';
import { type IProductState } from '../../reducers/product/types';

const normalize = (value: string): string => value.trim().toLowerCase();

const isSameCompany = (
  manufacturer: IManufacturer,
  responsiblePerson: IResponsiblePerson,
): boolean =>
  normalize(manufacturer.name) === normalize(responsiblePerson.name) &&
  normalize(manufacturer.street) === normalize(responsiblePerson.street) &&
  normalize(manufacturer.city) === normalize(responsiblePerson.city) &&
  manufacturer.countryType.isoCode2 === responsiblePerson.countryType.isoCode2;

export const countryName = (
  countryIsoCode2: string,
  languageCode: string,
): string => {
  try {
    return (
      new Intl.DisplayNames([languageCode], { type: 'region' }).of(
        countryIsoCode2,
      ) ?? countryIsoCode2
    );
  } catch {
    return countryIsoCode2;
  }
};

function OperatorAddress({
  operator,
  languageCode,
}: {
  operator: IManufacturer;
  languageCode: string;
}): React.JSX.Element {
  return (
    <address className="product-safety--address">
      <span className="product-safety--name">{operator.name}</span>
      <span>
        {operator.street}, {operator.zip} {operator.city},{' '}
        {countryName(operator.countryType.isoCode2, languageCode)}
      </span>
      {operator.email ? (
        <a href={`mailto:${operator.email}`}>{operator.email}</a>
      ) : null}
      {operator.website ? (
        <a href={operator.website} target="_blank" rel="noopener noreferrer">
          {operator.website}
        </a>
      ) : null}
    </address>
  );
}

export function ProductSafetyInformation({
  productSafety,
  languageCode,
  mpn,
  gtin,
}: {
  productSafety: IProductSafety | null | undefined;
  languageCode: string;
  mpn: string | null;
  gtin: string | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();

  const manufacturer: IManufacturer | null =
    productSafety?.manufacturer ?? null;
  const responsiblePerson: IResponsiblePerson | null =
    productSafety?.responsiblePerson ?? null;
  const showResponsiblePerson: boolean = Boolean(
    responsiblePerson &&
    !(manufacturer && isSameCompany(manufacturer, responsiblePerson)),
  );
  const safetyInformation: string | undefined =
    productSafety?.safetyInformation.find(
      (translation: ITranslation): boolean => translation.code === languageCode,
    )?.value;
  const documents: IProductSafetyDocument[] = (
    productSafety?.documents ?? []
  ).filter(
    (document: IProductSafetyDocument): boolean =>
      document.languageCode === null || document.languageCode === languageCode,
  );

  if (
    !manufacturer &&
    !responsiblePerson &&
    !mpn &&
    !gtin &&
    !safetyInformation &&
    documents.length === 0
  ) {
    return null;
  }

  return (
    <section className="product-safety" aria-labelledby="product-safety-title">
      <h2 id="product-safety-title">{t('productSafety.title')}</h2>
      <dl>
        {manufacturer ? (
          <>
            <dt>
              {t('productSafety.manufacturer')}
              {responsiblePerson && !showResponsiblePerson
                ? ` / ${t(`productSafety.responsiblePerson.${responsiblePerson.role}`)}`
                : ''}
            </dt>
            <dd>
              <OperatorAddress
                operator={manufacturer}
                languageCode={languageCode}
              />
            </dd>
          </>
        ) : null}

        {responsiblePerson && showResponsiblePerson ? (
          <>
            <dt>
              {t(`productSafety.responsiblePerson.${responsiblePerson.role}`)}
            </dt>
            <dd>
              <OperatorAddress
                operator={responsiblePerson}
                languageCode={languageCode}
              />
            </dd>
          </>
        ) : null}

        {mpn || gtin ? (
          <>
            <dt>{t('productSafety.identifiers')}</dt>
            <dd>
              {mpn ? (
                <span className="product-safety--identifier">
                  {t('productSafety.model')}: {mpn}
                </span>
              ) : null}
              {gtin ? (
                <span className="product-safety--identifier">
                  {t('productSafety.gtin')}: {gtin}
                </span>
              ) : null}
            </dd>
          </>
        ) : null}

        {safetyInformation ? (
          <>
            <dt>{t('productSafety.safetyInformation')}</dt>
            <dd className="product-safety--text">{safetyInformation}</dd>
          </>
        ) : null}

        {documents.length > 0 ? (
          <>
            <dt>{t('productSafety.documents')}</dt>
            <dd>
              <ul className="product-safety--documents">
                {documents.map(
                  (document: IProductSafetyDocument): React.JSX.Element => (
                    <li key={document.uuid}>
                      <a
                        href={document.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t(`productSafety.documentKind.${document.kind}`)} (
                        {document.filename})
                      </a>
                    </li>
                  ),
                )}
              </ul>
            </dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}

function ProductSafety({
  Product,
  Language,
}: {
  Product: IProductState;
  Language: ILanguageState;
}): React.JSX.Element | null {
  if (!Product.product) {
    return null;
  }

  return (
    <ProductSafetyInformation
      productSafety={Product.product.productSafety}
      languageCode={Language.languageType.code}
      mpn={Product.product.mpn}
      gtin={Product.product.gtin}
    />
  );
}

const mapStateToProps = ({
  Product,
  Language,
}: {
  Product: IProductState;
  Language: ILanguageState;
}) => ({
  Product,
  Language,
});

export default connect(mapStateToProps)(ProductSafety);
